import type { Editor } from 'grapesjs'

function initializeStyles(editor: Editor) {
  const sm = editor.StyleManager

  // Add general sector at position 0
  sm.addSector(
    'general',
    {
      name: 'General',
      open: false
    },
    { at: 0 }
  )

  // Add display property first
  sm.addProperty('general', {
    id: 'display',
    name: 'Display',
    property: 'display',
    type: 'select',
    default: 'block',
    options: [
      { id: 'block', label: 'Block' },
      { id: 'inline', label: 'Inline' },
      { id: 'inline-block', label: 'Inline Block' },
      { id: 'flex', label: 'Flex' },
      { id: 'inline-flex', label: 'Inline Flex' },
      { id: 'grid', label: 'Grid' },
      { id: 'inline-grid', label: 'Inline Grid' },
      { id: 'none', label: 'None' }
    ]
  })

  // Add other general properties
  sm.addProperty('general', {
    id: 'float',
    name: 'Float',
    property: 'float',
    type: 'select',
    default: 'none',
    options: [
      { id: 'none', label: 'None' },
      { id: 'left', label: 'Left' },
      { id: 'right', label: 'Right' }
    ]
  })

  sm.addProperty('general', {
    id: 'position',
    name: 'Position',
    property: 'position',
    type: 'select',
    default: 'static',
    options: [
      { id: 'static', label: 'Static' },
      { id: 'relative', label: 'Relative' },
      { id: 'absolute', label: 'Absolute' },
      { id: 'fixed', label: 'Fixed' },
      { id: 'sticky', label: 'Sticky' }
    ]
  })

  sm.addProperty('general', {
    id: 'top',
    name: 'Top',
    property: 'top',
    type: 'number',
    default: '0',
    units: ['px', '%', 'em', 'rem', 'vh']
  })

  sm.addProperty('general', {
    id: 'right',
    name: 'Right',
    property: 'right',
    type: 'number',
    default: '0px',
    units: ['px', '%', 'em', 'rem', 'vw']
  })

  sm.addProperty('general', {
    id: 'bottom',
    name: 'Bottom',
    property: 'bottom',
    type: 'number',
    default: '0px',
    units: ['px', '%', 'em', 'rem', 'vh']
  })

  sm.addProperty('general', {
    id: 'left',
    name: 'Left',
    property: 'left',
    type: 'number',
    default: '0px',
    units: ['px', '%', 'em', 'rem', 'vw']
  })

  // Add z-index property
  sm.addProperty('general', {
    id: 'z-index',
    name: 'Z-Index',
    property: 'z-index',
    type: 'number',
    default: 'auto',
    min: -10,
    max: 1000
  })

  // Note: Background sector is now handled by the StyleBgPlugin

  // Add flex sector
  sm.addSector(
    'flex',
    {
      name: 'Flex',
      open: false
    },
    { at: 1 } // Now at position 1 since background is removed
  )

  // Add properties to flex sector
  sm.addProperty('flex', {
    id: 'flex-direction',
    name: 'Direction',
    property: 'flex-direction',
    type: 'select',
    default: 'row',
    options: [
      { id: 'row', label: 'Row' },
      { id: 'row-reverse', label: 'Row Reverse' },
      { id: 'column', label: 'Column' },
      { id: 'column-reverse', label: 'Column Reverse' }
    ]
  })

  // Add other flex properties
  sm.addProperty('flex', {
    id: 'flex-wrap',
    name: 'Wrap',
    property: 'flex-wrap',
    type: 'select',
    default: 'nowrap',
    options: [
      { id: 'nowrap', label: 'Nowrap' },
      { id: 'wrap', label: 'Wrap' },
      { id: 'wrap-reverse', label: 'Wrap Reverse' }
    ]
  })

  sm.addProperty('flex', {
    id: 'justify-content',
    name: 'Justify Content',
    property: 'justify-content',
    type: 'select',
    default: 'flex-start',
    options: [
      { id: 'flex-start', label: 'Flex Start' },
      { id: 'flex-end', label: 'Flex End' },
      { id: 'center', label: 'Center' },
      { id: 'space-between', label: 'Space Between' },
      { id: 'space-around', label: 'Space Around' },
      { id: 'space-evenly', label: 'Space Evenly' }
    ]
  })

  // Number type properties with units
  sm.addProperty('flex', {
    id: 'align-items',
    name: 'Align Items',
    property: 'align-items',
    type: 'select',
    default: 'flex-start',
    options: [
      { id: 'flex-start', label: 'Flex Start' },
      { id: 'flex-end', label: 'Flex End' },
      { id: 'center', label: 'Center' },
      { id: 'baseline', label: 'Baseline' },
      { id: 'stretch', label: 'Stretch' }
    ]
  })

  sm.addProperty('flex', {
    id: 'align-content',
    name: 'Align Content',
    property: 'align-content',
    type: 'select',
    default: 'flex-start',
    options: [
      { id: 'flex-start', label: 'Flex Start' },
      { id: 'flex-end', label: 'Flex End' },
      { id: 'center', label: 'Center' },
      { id: 'space-between', label: 'Space Between' },
      { id: 'space-around', label: 'Space Around' },
      { id: 'space-evenly', label: 'Space Evenly' },
      { id: 'stretch', label: 'Stretch' }
    ]
  })

  sm.addProperty('flex', {
    id: 'gap-x',
    name: 'Gap X',
    property: 'column-gap',
    type: 'number',
    default: '0',
    units: ['px', '%', 'em', 'rem']
  })

  sm.addProperty('flex', {
    id: 'gap-y',
    name: 'Gap Y',
    property: 'row-gap',
    type: 'number',
    default: '0',
    units: ['px', '%', 'em', 'rem']
  })
}

export default initializeStyles